/**
 * What a member of Afriex staff may do through the admin API, one value per
 * capability. Which roles hold which permission is `adminRolePermissions`
 * (`utils/adminRolePermissions.ts`). Admin routes open to every staff role need
 * no permission and have none here.
 */
export var AfriexAdminPermission;
(function (AfriexAdminPermission) {
    // Users
    AfriexAdminPermission["UserUpdate"] = "USER.UPDATE";
    AfriexAdminPermission["UserTransactionLimitUpdate"] = "USER.TRANSACTION_LIMIT.UPDATE";
    AfriexAdminPermission["UserDeactivate"] = "USER.DEACTIVATE";
    AfriexAdminPermission["UserRoleUpdate"] = "USER.ROLE.UPDATE";
    AfriexAdminPermission["UserTierInfoRead"] = "USER.TIER_INFO.READ";
    AfriexAdminPermission["UserMarketingContactRead"] = "USER.MARKETING_CONTACT.READ";
    AfriexAdminPermission["UserTransactionRead"] = "USER.TRANSACTION.READ";
    AfriexAdminPermission["UserStatementRead"] = "USER.STATEMENT.READ";
    AfriexAdminPermission["UserBlock"] = "USER.BLOCK";
    AfriexAdminPermission["UserTierManage"] = "USER.TIER.MANAGE";
    AfriexAdminPermission["TransactionSwapCreate"] = "TRANSACTION.SWAP.CREATE";
    AfriexAdminPermission["SecurityNoteRead"] = "SECURITY_NOTE.READ";
    AfriexAdminPermission["SecurityNoteCreate"] = "SECURITY_NOTE.CREATE";
    AfriexAdminPermission["PushNotificationSend"] = "PUSH_NOTIFICATION.SEND";
    AfriexAdminPermission["KYCUpdate"] = "KYC.UPDATE";
    AfriexAdminPermission["KYCVerificationCreate"] = "KYC.VERIFICATION.CREATE";
    AfriexAdminPermission["BusinessDocumentRequestCreate"] = "BUSINESS.DOCUMENT_REQUEST.CREATE";
    AfriexAdminPermission["RFIRead"] = "RFI.READ";
    AfriexAdminPermission["RFIManage"] = "RFI.MANAGE";
    AfriexAdminPermission["BusinessVirtualAccountApplicationRead"] = "BUSINESS.VIRTUAL_ACCOUNT_APPLICATION.READ";
    AfriexAdminPermission["BusinessVirtualAccountApplicationReview"] = "BUSINESS.VIRTUAL_ACCOUNT_APPLICATION.REVIEW";
    AfriexAdminPermission["BlockedPaymentMethodRead"] = "BLOCKED_PAYMENT_METHOD.READ";
    AfriexAdminPermission["BlockedPaymentMethodManage"] = "BLOCKED_PAYMENT_METHOD.MANAGE";
    // Transactions
    AfriexAdminPermission["TransactionRead"] = "TRANSACTION.READ";
    AfriexAdminPermission["TransactionRefund"] = "TRANSACTION.REFUND";
    AfriexAdminPermission["TransactionReview"] = "TRANSACTION.REVIEW";
    AfriexAdminPermission["TransactionLedgerCreate"] = "TRANSACTION.LEDGER.CREATE";
    AfriexAdminPermission["TransactionRewardedRead"] = "TRANSACTION.REWARDED.READ";
    AfriexAdminPermission["TransactionPlaidRead"] = "TRANSACTION.PLAID.READ";
    AfriexAdminPermission["TransactionFeeRead"] = "TRANSACTION.FEE.READ";
    AfriexAdminPermission["TransactionFeeCreate"] = "TRANSACTION.FEE.CREATE";
    AfriexAdminPermission["DisputeRead"] = "DISPUTE.READ";
    AfriexAdminPermission["DisputeUpdate"] = "DISPUTE.UPDATE";
    // Rates and treasury
    AfriexAdminPermission["RatesUpload"] = "RATES.UPLOAD";
    AfriexAdminPermission["RatesHistoryRead"] = "RATES.HISTORY.READ";
    AfriexAdminPermission["RatePromoManage"] = "RATE_PROMO.MANAGE";
    AfriexAdminPermission["OTCVolumeRead"] = "OTC_VOLUME.READ";
    AfriexAdminPermission["OTCVolumeManage"] = "OTC_VOLUME.MANAGE";
    AfriexAdminPermission["OTCDeskRead"] = "OTC_DESK.READ";
    AfriexAdminPermission["OTCDeskManage"] = "OTC_DESK.MANAGE";
    AfriexAdminPermission["MarketPlaceRead"] = "MARKET_PLACE.READ";
    AfriexAdminPermission["MarketPlaceManage"] = "MARKET_PLACE.MANAGE";
    AfriexAdminPermission["ReferralPayoutManage"] = "REFERRAL.PAYOUT.MANAGE";
    AfriexAdminPermission["ReferralManualPayoutCreate"] = "REFERRAL.MANUAL_PAYOUT.CREATE";
    AfriexAdminPermission["ReferralTierManage"] = "REFERRAL_TIER.MANAGE";
    AfriexAdminPermission["ReferralTierUpdate"] = "REFERRAL_TIER.UPDATE";
    AfriexAdminPermission["RewardRead"] = "REWARD.READ";
    AfriexAdminPermission["RewardManage"] = "REWARD.MANAGE";
    // Processors
    AfriexAdminPermission["ProcessorTest"] = "PROCESSOR.TEST";
    AfriexAdminPermission["ProcessorDowntimeNotify"] = "PROCESSOR.DOWNTIME.NOTIFY";
    AfriexAdminPermission["PaymentMethodCardIssuerUpdate"] = "PAYMENT_METHOD.CARD_ISSUER.UPDATE";
    AfriexAdminPermission["ProcessorNameResolutionInvalidate"] = "PROCESSOR.NAME_RESOLUTION.INVALIDATE";
    // Settings
    AfriexAdminPermission["CountryUpdate"] = "COUNTRY.UPDATE";
    AfriexAdminPermission["CurrencyUpdate"] = "CURRENCY.UPDATE";
    AfriexAdminPermission["BusinessCountryUpdate"] = "BUSINESS_COUNTRY.UPDATE";
    // Businesses
    AfriexAdminPermission["BusinessWalletUpdate"] = "BUSINESS.WALLET.UPDATE";
    AfriexAdminPermission["BusinessTransactionUpdate"] = "BUSINESS.TRANSACTION.UPDATE";
    AfriexAdminPermission["BusinessFeeManage"] = "BUSINESS.FEE.MANAGE";
    AfriexAdminPermission["BusinessLimitManage"] = "BUSINESS.LIMIT.MANAGE";
    AfriexAdminPermission["FeatureManage"] = "FEATURE.MANAGE";
    AfriexAdminPermission["BusinessKYCUpdate"] = "BUSINESS.KYC.UPDATE";
    AfriexAdminPermission["BusinessFlagUpdate"] = "BUSINESS.FLAG.UPDATE";
    AfriexAdminPermission["BusinessUserFlagUpdate"] = "BUSINESS.USER_FLAG.UPDATE";
    AfriexAdminPermission["BusinessUserStatementRead"] = "BUSINESS.USER_STATEMENT.READ";
    AfriexAdminPermission["BusinessOnboardingManage"] = "BUSINESS.ONBOARDING.MANAGE";
    AfriexAdminPermission["BusinessPoolAccountManage"] = "BUSINESS.POOL_ACCOUNT.MANAGE";
    AfriexAdminPermission["BusinessFeaturesUpdate"] = "BUSINESS.FEATURES.UPDATE";
    AfriexAdminPermission["BusinessPostFundingUpdate"] = "BUSINESS.POST_FUNDING.UPDATE";
    AfriexAdminPermission["BusinessDangerZoneManage"] = "BUSINESS.DANGER_ZONE.MANAGE";
    AfriexAdminPermission["BusinessLiveAccessRead"] = "BUSINESS.LIVE_ACCESS.READ";
    AfriexAdminPermission["BusinessLiveAccessUpdate"] = "BUSINESS.LIVE_ACCESS.UPDATE";
    AfriexAdminPermission["BusinessAgreementRead"] = "BUSINESS.AGREEMENT.READ";
    AfriexAdminPermission["BusinessAgreementUpdate"] = "BUSINESS.AGREEMENT.UPDATE";
    AfriexAdminPermission["BusinessFeeNegotiationRead"] = "BUSINESS.FEE_NEGOTIATION.READ";
    AfriexAdminPermission["BusinessFeeNegotiationUpdate"] = "BUSINESS.FEE_NEGOTIATION.UPDATE";
    AfriexAdminPermission["BusinessPoolDepositReview"] = "BUSINESS.POOL_DEPOSIT.REVIEW";
    AfriexAdminPermission["BusinessInvoiceManage"] = "BUSINESS.INVOICE.MANAGE";
    AfriexAdminPermission["SettlementPaymentMethodManage"] = "SETTLEMENT_PAYMENT_METHOD.MANAGE";
    AfriexAdminPermission["BusinessPinResetRequestReview"] = "BUSINESS.PIN_RESET_REQUEST.REVIEW";
    AfriexAdminPermission["BusinessAffiliateRead"] = "BUSINESS.AFFILIATE.READ";
    AfriexAdminPermission["BusinessAffiliateManage"] = "BUSINESS.AFFILIATE.MANAGE";
    AfriexAdminPermission["BusinessUpdateRead"] = "BUSINESS_UPDATE.READ";
    AfriexAdminPermission["BusinessUpdateManage"] = "BUSINESS_UPDATE.MANAGE";
    AfriexAdminPermission["SubscriptionPlanRead"] = "SUBSCRIPTION_PLAN.READ";
    AfriexAdminPermission["SubscriptionPlanManage"] = "SUBSCRIPTION_PLAN.MANAGE";
    // Engineering
    // The BullMQ dashboard (/admin/queues), guarded in app.ts rather than a router.
    AfriexAdminPermission["QueueDashboardRead"] = "QUEUE_DASHBOARD.READ";
})(AfriexAdminPermission || (AfriexAdminPermission = {}));
//# sourceMappingURL=AfriexAdminPermission.js.map